import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import getColor from "@insite/mobius/utilities/getColor";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import { Amount, pxToRem, pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import { basePixelAmount } from "@store/Framework/Common/Utilities/unitHelper";
import Styleguide from "@store/Framework/Types/Styleguide";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { css } from "styled-components";

const titleStyles = (breakpoints: Array<Amount | null>) => css`
    ${spacingStyles("1em", "0.5em")}
    ${breakpointValues(pxToRemArray(breakpoints), "font-size")}
    ${({ theme }) => `
        font-family: ${theme.design.typography.fontFamily.title};
        font-weight: ${theme.design.typography.weight.bold};
    `}
`;

const typography: Styleguide["typography"] = {
    fontFamilyImportUrl:
        "https://fonts.googleapis.com/css2?family=Titillium+Web:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&display=swap",
    body: {
        size: "inherit",
        weight: "inherit",
        fontFamily: "inherit",
        css: css`
            color: ${getColor("text")};
            font-style: normal;
            text-transform: none;

            ${({ theme }) => `
                font-family: ${theme.design.typography.fontFamily.base};
                font-weight: ${theme.design.typography.weight.base};
                font-size: ${theme.design.typography.size.base};
                line-height: ${theme.design.typography.lineHeight.base};
            `}
        `,
    },
    p: {
        size: "inherit",
        weight: "inherit",
        css: spacingStyles(),
    },
    h1: {
        css: css`
            ${titleStyles([31, null, 37, null, 48])}
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    h2: {
        css: css`
            ${titleStyles([26, null, 31, null, 37])}
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    h3: {
        css: css`
            ${titleStyles([21, null, 26, null, 31])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    h4: {
        css: css`
            ${titleStyles([18, null, 21, null, 26])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    h5: {
        css: css`
            ${titleStyles([16, null, 18, null, 21])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    h6: {
        css: css`
            ${titleStyles([16, null, 17, null, 20])}
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    legend: {
        size: "inherit",
        weight: "inherit",
        color: null as any,
        transform: null as any,
        css: css`
            color: ${getColor("text.accent")};
            /* Store Removed - text-transform */

            ${({ theme }) => `
                font-size: ${theme.design.typography.size.smallest};
     		/* Store Removed - font-weight */
                line-height: ${theme.design.typography.lineHeight.base};
            `};

            && + ${FormInputWrapper}, && + ${FormFieldStyle} {
                margin-top: ${pxToRem(3)};
            }
        `,
    },
    bannerText: {
        css: css`
            ${spacingStyles()}
            ${({ theme }) => css`
                ${breakpointValues(
                    [null, null, theme.design.typography.size.large, null, theme.design.typography.size.larger],
                    "font-size",
                )}
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },
    ".fr-view": {
        css: css`
            &:not(#important) {
                ${({ theme }) => css`
                    ${theme.typography.body.css}
                    font-size: ${basePixelAmount}px;
                    max-width: calc(${theme.design.layout.contentWidth}px + 40px);
                    padding: 20px;
                    margin: 0 auto;

                    ul {
                        ${theme.lists.defaultProps?.css}
                        ${theme.lists.unorderedListProps?.css}
                    }

                    ol {
                        ${theme.lists.defaultProps?.css}
                        ${theme.lists.orderedListProps?.css}
                    }

                    li {
                        ${theme.lists.listItemProps?.css}
                    }

                    a {
                        ${theme.link.defaultProps?.css}
                    }
                `}
            }
        `,
    },
};

typography.bannerTitle = { ...typography.h1, forwardAs: "h1" };
typography.bannerSecondaryTitle = { ...typography.h2, forwardAs: "h1" };

export default typography;
